import React, { useEffect } from 'react';
import GiftCardBalance from './balance';
import { Box, Center, VStack, HStack, ScrollView, useDisclose, useTheme, Actionsheet, Skeleton } from 'native-base';
import { useGeneralStyles } from '~/shared/styles';
import CardHolder from './Card/CardHolder';
import ActionSheetContent from './ActionSheetContent';
import { useEffectOnce } from 'usehooks-ts';
import {
    GIFTCARD_FLOWS,
    observer,
    useSpringBigStore,
    useGetGiftCardList,
    GiftCard,
    useGetMember,
    useI18n,
    useDeleteGiftCards,
} from '@springbig/shared';
import NoCards from './Card/NoCards';
import Section from '~/shared/Section';
import PaginationDot from 'react-native-animated-pagination-dot';
import { Caption, H3 } from '~/shared/Typography';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Mousewheel, Pagination, Keyboard } from 'swiper';
import Button from '~/shared/Button';
import { SpIcon } from "~/shared/Icon";
import { faTrash } from '@fortawesome/free-solid-svg-icons';
import DeleteCardsModal from './DeleteCardsModal';
import { useToast } from '~/hooks/toast';
import EmailVerificationForm from '~/shared/EmailVerification/EmailVerificationForm';
import { isAfter, addDays } from 'date-fns';
import { SkeletonCard } from './Card';


const SpringbigPayScreen = () => {

    const [activeSlide, setActiveSlide] = React.useState(0);
    const [currentActionSheetItem, setCurrentActionSheetItem] = React.useState<GIFTCARD_FLOWS>(GIFTCARD_FLOWS.null);
    const [currentFlow, setCurrentFlow] = React.useState<GIFTCARD_FLOWS[]>([]);
    const [passedData, setPassedData] = React.useState<any>(null);
    const [manageGiftCard, setManageGiftCard] = React.useState<GiftCard | null>(null);
    const [hasAccess, setAccess] = React.useState<boolean>(false);
    const [isDeleteCardsModal, setIsDeleteCardsModal] = React.useState<boolean>(false);
    const [isEmailVerification, setIsEmailVerification] = React.useState<boolean>(false);
    const [isDismissedEmailPrompt, setIsDismissedEmailPrompt] = React.useState<boolean>(false);

    const gStyle = useGeneralStyles();
    const { giftCards, member, merchant, walletId } = useSpringBigStore();
    const theme = useTheme();
    const { t } = useI18n();
    const { successToast } = useToast();

    const {
        isOpen,
        onOpen,
        onClose
    } = useDisclose();

    const { refetch: refreshMember } = useGetMember();
    const { refetch: giftCardListQuery, isLoading: isGiftCardLoading } = useGetGiftCardList();
    const { mutate: deleteGiftCards, isLoading } = useDeleteGiftCards();

    const onDeleteGiftCards = (): void => {

        const payload = member && {
            member_id: member?.id.toString(),
        };

        deleteGiftCards(payload, {
            onSuccess() {

                successToast('Cards successfully deleted');
                setIsDeleteCardsModal(false);
                springbigPayRefresh();
                refreshMember();
            }
        });
    };

    const springbigPayRefresh = () => {

        giftCardListQuery();
    };

    useEffect(() => {

        springbigPayRefresh();

        const _flow: GIFTCARD_FLOWS[] = [];


        observer.on('on-set-primary-gift-card', () => {

            giftCardListQuery();
            setActiveSlide(0);
        });

        observer.on('close-actionsheet', () => {

            giftCardListQuery();
            refreshMember();
            onClose();
            _flow.length = 0;
            setCurrentActionSheetItem(GIFTCARD_FLOWS.null);
            setCurrentFlow([]);
        });

        observer.on('open-actionsheet', (flow: GIFTCARD_FLOWS) => {

            onOpen();
            setCurrentActionSheetItem(flow);
            _flow.push(flow);
            setCurrentFlow([flow]);
        });

        observer.on('change-screen', (flow: GIFTCARD_FLOWS) => {

            setCurrentActionSheetItem(flow);
            _flow.push(flow);
            setCurrentFlow(_flow);
        });

        observer.on('sent-giftcard', ({ email, amount }) => {
            setPassedData({ email, amount });
            setCurrentActionSheetItem(GIFTCARD_FLOWS.sentGift);
            setCurrentFlow([GIFTCARD_FLOWS.sentGift]);
        });

        observer.on('giftcard-back', () => {

            _flow.pop();
            setCurrentActionSheetItem(_flow[_flow.length - 1]);
            setCurrentFlow(_flow);

        });

        observer.on('on-manage-cash', (giftCard: GiftCard) => {

            setManageGiftCard(giftCard);
        });

        observer.on('on-manage-auto-reload', (giftCard: GiftCard) => {

            setManageGiftCard(giftCard);
        });

        return () => {

            observer.off('change-screen');
            observer.off('open-actionsheet');
            observer.off('close-actionsheet');
            observer.off('giftcard-back');
            observer.off('on-set-primary-gift-card');
        };
    }, []);

    const sortedGiftCard = giftCards.sort((a, b) => (a.primary_card === b.primary_card ? 0 : a.primary_card ? -1 : 1));

    useEffect(() => {

        if (!member) return;

        setTimeout(() => {

            setAccess(!!member.gift_card_enabled);
        }, 1000);
    }, [member]);

    useEffect(() => {

        if (member) {
            setIsEmailVerification(
                !member.consumer.email_validated && (
                    !localStorage.getItem('last_shown_email_verification_prompt')
                    ||
                    isAfter(
                        new Date(),
                        addDays(new Date(
                            JSON.parse(
                                localStorage.getItem('last_shown_email_verification_prompt')
                            )
                        ), 4)
                    )
                )
            );
        }
    }, []);

    const onCancelEMailPrompt = (): void => {

        localStorage.setItem(
            'last_shown_email_verification_prompt',
            JSON.stringify(new Date())
        );
        setIsDismissedEmailPrompt(true);
    };

    if (isGiftCardLoading || (!member && !hasAccess) || !hasAccess) {
        return <SpringbigPaySkeleton />;
    }

    return (
        <Section flex={1}>
            {hasAccess ?
                <ScrollView pb={100} >
                    <Actionsheet isOpen={isEmailVerification && !isDismissedEmailPrompt} onClose={() => console.log('hey')} hideDragIndicator>
                        <Actionsheet.Content backgroundColor={gStyle.section_background} zIndex={9999} overflow='auto' w={'clamp(320px, 100vw, 480px)'} alignSelf='center'>
                            <VStack space={4} mt={4}>
                                <H3 textAlign={'center'}>{t('giftCard.emailVerificationTitle')}</H3>
                                <EmailVerificationForm
                                    t={t}
                                    walletId={walletId}
                                    onCancel={onCancelEMailPrompt}
                                    onProceed={onCancelEMailPrompt}
                                />
                            </VStack>
                        </Actionsheet.Content>
                    </Actionsheet>
                    <GiftCardBalance />
                    <Box
                        width='96%'
                        backgroundColor={gStyle.section_background}
                        alignSelf='center'
                        borderRadius={8}
                        shadow={3}
                        mt={8}
                    >
                        <Swiper
                            spaceBetween={0}
                            navigation={false}
                            keyboard={true}
                            modules={[Pagination, Mousewheel, Keyboard]}
                            className="mySwiper"
                            style={{
                                pointerEvents: 'auto',
                                width: '100%',
                            }}
                            onSlideChange={(swiper) => {

                                setActiveSlide(swiper.snapIndex)
                            }}
                        >

                            {(sortedGiftCard && sortedGiftCard.length > 0) ? sortedGiftCard.map((card, index) => (
                                <SwiperSlide
                                    style={{
                                        width: '100%',
                                        height: '100%',
                                    }}
                                    key={index}
                                >
                                    <CardHolder captionColor={gStyle.button_background} cardContent={card} />
                                </SwiperSlide>
                            )) : <SwiperSlide><NoCards /></SwiperSlide>}

                        </Swiper>
                    </Box>
                    <Center mt={3}>
                        <PaginationDot
                            activeDotColor={theme?.colors?.primary || 'white'}
                            curPage={activeSlide}
                            maxPage={(giftCards ? giftCards.length : 1)}
                        />
                    </Center>
                    <VStack justifyContent={'center'} pt={2}>
                        {giftCards.length > 1 && giftCards.some(giftCard => giftCard.balance === '$0.00') &&
                            <Button
                                color={gStyle.button_background}
                                onPress={() => setIsDeleteCardsModal(true)}
                            >
                                <SpIcon size={12} icon={faTrash}></SpIcon>
                            </Button>
                        }
                        <HStack justifyContent={'center'} flexWrap={'wrap'}>
                            {merchant && !merchant.hide_gift_card_payments &&
                                <Button
                                    onPress={() => observer.emit('open-actionsheet', GIFTCARD_FLOWS.sendGiftCard)}
                                >
                                    <Caption color={gStyle.button_background}>{t('giftCard.sendToFriend', [merchant?.gift_card_alias])}</Caption>
                                </Button>
                            }
                            <Button
                                onPress={() => observer.emit('open-actionsheet', GIFTCARD_FLOWS.existingGiftCard)}
                            >
                                <Caption color={gStyle.button_background}>{t('giftCard.importExistingButton', [merchant?.gift_card_alias])}</Caption>
                            </Button>
                        </HStack>
                    </VStack>
                    {isDeleteCardsModal &&
                        <DeleteCardsModal
                            onCancel={() => setIsDeleteCardsModal(false)}
                            onSubmit={onDeleteGiftCards}
                            isLoading={isLoading}
                        />
                    }
                    <ActionSheetContent
                        currentActionSheetItem={currentActionSheetItem}
                        isOpen={isOpen}
                        currentFlow={currentFlow}
                        data={passedData}
                        manageGiftCard={manageGiftCard}
                    />
                </ScrollView>
                :
                <Box alignItems={'center'} justifyContent={'center'} h={'100%'} >no access</Box>
            }
        </Section>
    );
};

const SpringbigPaySkeleton = () => {

    const gStyle = useGeneralStyles();

    return (
        <VStack >
            <HStack mt={'2rem'} alignSelf={'center'}>
                <Box alignItems={'center'}>
                    <Skeleton h={10} w={'8rem'} rounded={'lg'} />
                    <Skeleton h={'1.7rem'} w={'8rem'} rounded={'lg'} mt={'0.5rem'}/>
                    <Skeleton.Text lines={2} w='6rem' m={2} />
                </Box>
                <Box
                    borderLeftColor={gStyle.button_background}
                    borderLeftWidth={0.60}
                    h='70%'
                    alignSelf={'center'}
                    mx={'2rem'}
                />
                <Box alignItems={'center'}> 
                    <Skeleton h={10} w={'8rem'} rounded={'lg'} />
                    <Skeleton.Text lines={2} w='6rem' m={2} />
                </Box>
            </HStack>
            <Box 
                 width='96%'
                 backgroundColor={gStyle.section_background}
                 alignSelf='center'
                 borderRadius={8}
                 shadow={3}
                 mt={8}
            >
                <SkeletonCard />
            </Box>
            <Skeleton h={4} w={'6rem'} rounded={'full'} borderRadius={5} my={'2rem'} alignSelf={'center'}/>
        </VStack>
    )
}

export default SpringbigPayScreen;
